import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { useAuth } from './AuthContext';
import { format, parse, subYears, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz'; // Updated import
import Spinner from './Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faDollarSign, faClipboardList, faChartBar, faFileInvoice, faChartPie } from '@fortawesome/free-solid-svg-icons';

// Configuration
const policyDeductionConfig = [
  { policyNumber: 334, deductionPercentage: 1.0 },
];

const contractProfitSplit = [
  { contractId: 745687, splitPercentage: 0.5 }, // Fuellink
];

const contractMapping = [
  { contractId: 273898, description: 'GO TRANSPORT LLC' },
  { contractId: 392261, description: 'eCapital' },
  { contractId: 425680, description: 'C Fuel' },
  { contractId: 492191, description: 'BG' },
  { contractId: 684747, description: 'FBT' },
  { contractId: 684748, description: 'Fila Mar' },
  { contractId: 745687, description: 'Fuellink' },
  { contractId: 753698, description: 'AQHA' },
  { contractId: 793342, description: 'CANADIAN' },
];

const commissionPolicyMapping = {
  333: 'AQHA',
  334: 'Integrity Factoring',
};

// Utility Functions
const formatMonthYear = (dateString) => {
  const date = parse(`${dateString}-01`, 'yyyy-MM-dd', new Date());
  return format(date, 'MMM-yyyy');
};

const getContractDescription = (contractId) => {
  const contract = contractMapping.find((item) => item.contractId === contractId);
  return contract ? contract.description : contractId;
};

function computeBillingData(usageData, subscriptionData, profitData, commissionData) {
  const profitMap = {};
  profitData.forEach((p) => {
    const key = `${p.Month}-${p.ContractId}`;
    profitMap[key] = p.TotalProfit;
  });

  const commissionMap = {};
  commissionData.forEach((c) => {
    const key = `${c.Month}-${c.policyNumber}`;
    commissionMap[key] = c.TotalCommission;
  });

  const billingDataMap = {};

  usageData.forEach((item) => {
    const { monthKey, month, contractId, transactionCount } = item;
    const key = `${monthKey}-${contractId}`;

    if (!billingDataMap[key]) {
      billingDataMap[key] = {
        monthKey,
        month,
        contractId,
        totalTransactionCount: 0,
        transactionFees: 0.0,
        subscriptionFees: 0.0,
        profitShare: 0.0,
        billingAmount: 0.0,
      };
    }

    billingDataMap[key].totalTransactionCount += transactionCount;
  });

  subscriptionData.forEach((subscription) => {
    const { MonthlyAmount, StartDate, EndDate, SubscriptionName } = subscription;
    const start = startOfDay(toZonedTime(new Date(StartDate), 'America/Chicago')); // CST
    const end = EndDate ? startOfDay(toZonedTime(new Date(EndDate), 'America/Chicago')) : new Date();
    const contractId = SubscriptionName.includes('BG') ? 492191 : SubscriptionName.includes('AQHA') ? 753698 : null;

    if (!contractId) return;

    let currentMonth = new Date(start.getFullYear(), start.getMonth(), 1);
    const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);

    while (currentMonth <= endMonth) {
      const monthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
      const key = `${monthKey}-${contractId}`;

      if (!billingDataMap[key]) {
        billingDataMap[key] = {
          monthKey,
          month: formatMonthYear(monthKey),
          contractId,
          totalTransactionCount: 0,
          transactionFees: 0.0,
          subscriptionFees: 0.0,
          profitShare: 0.0,
          billingAmount: 0.0,
        };
      }

      billingDataMap[key].subscriptionFees += MonthlyAmount;
      currentMonth.setMonth(currentMonth.getMonth() + 1);
    }
  });

  const rateChangeDate = new Date('2025-03-01');
  Object.values(billingDataMap).forEach((item) => {
    if (item.contractId !== 273898) {
      const transactionCount = item.totalTransactionCount;
      let transactionFee = 0.0;
      const monthDate = parse(`${item.monthKey}-01`, 'yyyy-MM-dd', new Date());

      if (monthDate < rateChangeDate) {
        if (transactionCount > 100) {
          transactionFee = Math.ceil((transactionCount - 100) / 100) * 50.0;
        }
      } else {
        transactionFee = transactionCount * 0.75;
      }
      item.transactionFees = transactionFee;
    }
  });

  profitData.forEach((p) => {
    const monthKey = p.Month;
    const contractId = p.ContractId;
    const key = `${monthKey}-${contractId}`;
    if (!billingDataMap[key]) return;

    let remainingProfit = p.TotalProfit || 0.0;

    policyDeductionConfig.forEach((policyCfg) => {
      const commissionKey = `${monthKey}-${policyCfg.policyNumber}`;
      if (commissionMap[commissionKey]) {
        const commissionAmt = commissionMap[commissionKey];
        remainingProfit -= commissionAmt * policyCfg.deductionPercentage;
      }
    });

    const splitCfg = contractProfitSplit.find((c) => c.contractId === contractId);
    if (splitCfg) {
      const share = remainingProfit * splitCfg.splitPercentage;
      billingDataMap[key].profitShare += share;
    }
  });

  Object.values(billingDataMap).forEach((item) => {
    item.billingAmount = item.transactionFees + item.subscriptionFees + item.profitShare;
  });

  return Object.values(billingDataMap);
}

const UsageStats = () => {
  const { currentUser } = useAuth();
  const [startDate, setStartDate] = useState(format(subYears(new Date(), 1), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedContract, setSelectedContract] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [usageData, setUsageData] = useState([]);
  const [billingData, setBillingData] = useState([]);
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [profitData, setProfitData] = useState([]);
  const [commissionData, setCommissionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('usage');
  const [showAllUsage, setShowAllUsage] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;

  const isWithinDateRange = (monthKey) => {
    const [year, month] = monthKey.split('-').map(Number);
    const itemDate = new Date(year, month - 1, 1);
    const start = startDate ? new Date(startDate) : new Date(0);
    const end = endDate ? new Date(endDate) : new Date();
    return itemDate >= start && itemDate <= end;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser?.userId) return;

      setLoading(true);
      try {
        const response = await fetch(`${apiUrl}/dataServer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'getMonthlyUsageData',
            payload: { userId: currentUser?.userId },
          }),
        });

        if (response.ok) {
          const fetchedData = await response.json();
          const { monthlyUsageData, subscriptionData, profitData, commissionData } = fetchedData;

          const filteredUsageData = monthlyUsageData
            .filter((item) => isWithinDateRange(item.Month) && 
              (selectedContract === 'all' || item.ContractId === parseInt(selectedContract)))
            .map((item) => ({
              monthKey: item.Month,
              month: formatMonthYear(item.Month),
              contract: getContractDescription(item.ContractId),
              contractId: item.ContractId,
              transactionCount: item.TransactionCount,
              totalAmount: item.TotalAmount,
              totalGallons: item.TotalGallons,
            }));

          const unfilteredUsageData = monthlyUsageData.map((item) => ({
            monthKey: item.Month,
            month: formatMonthYear(item.Month),
            contractId: item.ContractId,
            transactionCount: item.TransactionCount,
            totalGallons: item.TotalGallons,
          }));

          const computedBillingData = computeBillingData(unfilteredUsageData, subscriptionData, profitData, commissionData);

          const filteredBillingData = computedBillingData.filter((item) => 
            isWithinDateRange(item.monthKey) && 
            (selectedContract === 'all' || item.contractId === parseInt(selectedContract)));

          const filteredProfitData = profitData.filter((item) => isWithinDateRange(item.Month));
          const filteredCommissionData = commissionData.filter((item) => isWithinDateRange(item.Month));

          setUsageData(filteredUsageData);
          setBillingData(filteredBillingData);
          setSubscriptionData(subscriptionData);
          setProfitData(filteredProfitData);
          setCommissionData(filteredCommissionData);
        } else {
          setAlertMessage(`Server Error: ${await response.text()}`);
        }
      } catch (error) {
        setAlertMessage(`Error: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser?.userId, startDate, endDate, selectedContract, apiUrl]);

  const getUsageChartData = () => {
    const chartDataMap = {};
    usageData.forEach((item) => {
      const { month, transactionCount, totalAmount, totalGallons } = item;
      if (!chartDataMap[month]) {
        chartDataMap[month] = {
          month,
          transactionCount: 0,
          totalAmount: 0,
          totalGallons: 0,
        };
      }
      chartDataMap[month].transactionCount += transactionCount;
      chartDataMap[month].totalAmount += totalAmount;
      chartDataMap[month].totalGallons += totalGallons || 0;
    });
    return Object.values(chartDataMap);
  };

  const getBillingChartData = () => {
    const chartDataMap = {};
    billingData.forEach((item) => {
      const { monthKey, month, transactionFees, subscriptionFees, profitShare } = item;
      if (!chartDataMap[monthKey]) {
        chartDataMap[monthKey] = {
          month,
          transactionFees: 0,
          subscriptionFees: 0,
          profitShare: 0,
          total: 0,
        };
      }
      chartDataMap[monthKey].transactionFees += transactionFees;
      chartDataMap[monthKey].subscriptionFees += subscriptionFees;
      chartDataMap[monthKey].profitShare += profitShare;
      chartDataMap[monthKey].total += transactionFees + subscriptionFees + profitShare;
    });
    return Object.values(chartDataMap);
  };

  const getInvoiceSummary = (month) => {
    const monthData = billingData.filter((item) => item.monthKey === month);
    return monthData.map((item) => ({
      contract: getContractDescription(item.contractId),
      transactionCount: item.totalTransactionCount,
      transactionFees: item.transactionFees,
      subscriptionFees: item.subscriptionFees,
      profitShare: item.profitShare,
      total: item.billingAmount,
    }));
  };

  const uniqueMonths = [...new Set(billingData.map((item) => item.monthKey))].sort();

  const customBillingTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const total = data.transactionFees + data.subscriptionFees + data.profitShare;
      return (
        <div className="bg-white p-2 border rounded shadow">
          <p className="font-bold">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: ${entry.value.toFixed(2)}
            </p>
          ))}
          <p className="font-bold">Total: ${total.toFixed(2)}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h2 className="text-2xl font-bold mb-6">Usage Statistics</h2>

      {/* Filters */}
      <div className="mb-6 flex gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="font-semibold">Start Date:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="font-semibold">End Date:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="font-semibold">Contract:</label>
          <select
            value={selectedContract}
            onChange={(e) => setSelectedContract(e.target.value)}
            className="border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
          >
            <option value="all">All</option>
            {contractMapping.map((contract) => (
              <option key={contract.contractId} value={contract.contractId}>
                {contract.description}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <>
          {/* Tab Headers */}
          <div className="flex gap-4 mb-6">
            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === 'usage'
                  ? 'bg-gradient-to-r from-[var(--primary-color)] to-[var(--secondary-color)] text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setActiveTab('usage')}
            >
              <FontAwesomeIcon icon={faChartLine} /> Usage
            </button>
            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === 'billing'
                  ? 'bg-gradient-to-r from-[var(--primary-color)] to-[var(--secondary-color)] text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setActiveTab('billing')}
            >
              <FontAwesomeIcon icon={faDollarSign} /> Billing
            </button>
            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === 'subscriptions'
                  ? 'bg-gradient-to-r from-[var(--primary-color)] to-[var(--secondary-color)] text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setActiveTab('subscriptions')}
            >
              <FontAwesomeIcon icon={faClipboardList} /> Subscriptions
            </button>
            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === 'commissions'
                  ? 'bg-gradient-to-r from-[var(--primary-color)] to-[var(--secondary-color)] text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setActiveTab('commissions')}
            >
              <FontAwesomeIcon icon={faFileInvoice} /> Commissions
            </button>
            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === 'analytics'
                  ? 'bg-gradient-to-r from-[var(--primary-color)] to-[var(--secondary-color)] text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setActiveTab('analytics')}
            >
              <FontAwesomeIcon icon={faChartPie} /> Analytics
            </button>
          </div>

          {/* Usage Tab */}
          {activeTab === 'usage' && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                <div className="bg-white p-4 rounded-lg shadow-md">
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon={faChartLine} /> Monthly Usage (Line Chart)
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={getUsageChartData()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" tickFormatter={(value) => `$${value / 1000}K`} domain={['auto', 'auto']} />
                      <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => `${value}`} domain={['auto', 'auto']} />
                      <Tooltip
                        formatter={(value, name) =>
                          name === 'totalAmount'
                            ? `$${new Intl.NumberFormat('en').format(value.toFixed(2))}`
                            : new Intl.NumberFormat('en').format(value)
                        }
                        labelFormatter={(label) => `${label}`}
                      />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="totalAmount" stroke="var(--primary-color)" name="Total Amount ($)" />
                      <Line yAxisId="right" type="monotone" dataKey="transactionCount" stroke="var(--secondary-color)" name="Transaction Count" />
                      <Line yAxisId="right" type="monotone" dataKey="totalGallons" stroke="#82ca9d" name="Total Gallons" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-md">
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon={faChartBar} /> Monthly Usage (Bar Chart)
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={getUsageChartData()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" tickFormatter={(value) => `$${value / 1000}K`} domain={['auto', 'auto']} />
                      <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => `${value}`} domain={['auto', 'auto']} />
                      <Tooltip
                        formatter={(value, name) =>
                          name === 'totalAmount'
                            ? `$${new Intl.NumberFormat('en').format(value.toFixed(2))}`
                            : new Intl.NumberFormat('en').format(value)
                        }
                        labelFormatter={(label) => `${label}`}
                      />
                      <Legend />
                      <Bar yAxisId="left" dataKey="totalAmount" fill="var(--primary-color)" name="Total Amount ($)" />
                      <Bar yAxisId="right" dataKey="transactionCount" fill="var(--secondary-color)" name="Transaction Count" />
                      <Bar yAxisId="right" dataKey="totalGallons" fill="#82ca9d" name="Total Gallons" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-md overflow-x-auto">
                <h3 className="text-xl font-semibold mb-4">Detailed Monthly Usage</h3>
                <button
                  className="mb-4 px-4 py-2 bg-blue-500 text-white rounded-lg"
                  onClick={() => setShowAllUsage(!showAllUsage)}
                >
                  {showAllUsage ? 'Show Last 12 Months' : 'Show All Data'}
                </button>
                <table className="min-w-full bg-white">
                  <thead className="bg-gray-200 text-gray-700">
                    <tr>
                      <th className="py-2 px-4 text-left text-sm font-semibold">Month</th>
                      <th className="py-2 px-4 text-left text-sm font-semibold">Contract</th>
                      <th className="py-2 px-4 text-left text-sm font-semibold">Transaction Count</th>
                      <th className="py-2 px-4 text-left text-sm font-semibold">Total Amount (USD)</th>
                      <th className="py-2 px-4 text-left text-sm font-semibold">Total Gallons</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageData.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="py-2 px-4 border-b border-gray-200 text-sm">{item.month}</td>
                        <td className="py-2 px-4 border-b border-gray-200 text-sm">{item.contract}</td>
                        <td className="py-2 px-4 border-b border-gray-200 text-sm">{item.transactionCount}</td>
                        <td className="py-2 px-4 border-b border-gray-200 text-sm">${item.totalAmount.toFixed(2)}</td>
                        <td className="py-2 px-4 border-b border-gray-200 text-sm">{item.totalGallons ? item.totalGallons.toFixed(2) : 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Billing Tab */}
          {activeTab === 'billing' && (
            <div>
              <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon={faDollarSign} /> Monthly Billing
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={getBillingChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(value) => `$${value.toFixed(0)}`} />
                    <Tooltip content={customBillingTooltip} />
                    <Legend />
                    <Line type="monotone" dataKey="transactionFees" stroke="#8884d8" name="Transaction Fees" />
                    <Line type="monotone" dataKey="subscriptionFees" stroke="#82ca9d" name="Subscription Fees" />
                    <Line type="monotone" dataKey="profitShare" stroke="#ffc107" name="Profit Share" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-md mb-6 overflow-x-auto">
                <h3 className="text-xl font-semibold mb-4">Billing Details by Contract</h3>
                <table className="min-w-full bg-white">
                  <thead className="bg-gray-200 text-gray-700">
                    <tr>
                      <th className="py-2 px-4 text-left text-sm font-semibold">Month</th>
                      <th className="py-2 px-4 text-left text-sm font-semibold">Contract</th>
                      <th className="py-2 px-4 text-left text-sm font-semibold">Transaction Count</th>
                      <th className="py-2 px-4 text-left text-sm font-semibold">Transaction Fees (USD)</th>
                      <th className="py-2 px-4 text-left text-sm font-semibold">Subscription Fees (USD)</th>
                      <th className="py-2 px-4 text-left text-sm font-semibold">Profit Share (USD)</th>
                      <th className="py-2 px-4 text-left text-sm font-semibold">Total (USD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billingData.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="py-2 px-4 border-b border-gray-200 text-sm">{item.month}</td>
                        <td className="py-2 px-4 border-b border-gray-200 text-sm">{getContractDescription(item.contractId)}</td>
                        <td className="py-2 px-4 border-b border-gray-200 text-sm">{item.totalTransactionCount}</td>
                        <td className="py-2 px-4 border-b border-gray-200 text-sm">${item.transactionFees.toFixed(2)}</td>
                        <td className="py-2 px-4 border-b border-gray-200 text-sm">${item.subscriptionFees.toFixed(2)}</td>
                        <td className="py-2 px-4 border-b border-gray-200 text-sm">${item.profitShare.toFixed(2)}</td>
                        <td className="py-2 px-4 border-b border-gray-200 text-sm">${item.billingAmount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-md overflow-x-auto">
                <h3 className="text-xl font-semibold mb-4">Billing Summary</h3>
                <div className="mb-4">
                  <label className="font-semibold mr-2">Select Month:</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="border p-2 rounded-lg"
                  >
                    {uniqueMonths.map((month) => (
                      <option key={month} value={month}>{formatMonthYear(month)}</option>
                    ))}
                  </select>
                </div>
                <table className="min-w-full bg-white">
                  <thead className="bg-gray-200 text-gray-700">
                    <tr>
                      <th className="py-2 px-4 text-left text-sm font-semibold">Contract</th>
                      <th className="py-2 px-4 text-left text-sm font-semibold">Transaction Count</th>
                      <th className="py-2 px-4 text-left text-sm font-semibold">Transaction Fees (USD)</th>
                      <th className="py-2 px-4 text-left text-sm font-semibold">Subscription Fees (USD)</th>
                      <th className="py-2 px-4 text-left text-sm font-semibold">Profit Share (USD)</th>
                      <th className="py-2 px-4 text-left text-sm font-semibold">Total (USD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getInvoiceSummary(selectedMonth).map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="py-2 px-4 border-b border-gray-200 text-sm">{item.contract}</td>
                        <td className="py-2 px-4 border-b border-gray-200 text-sm">{item.transactionCount}</td>
                        <td className="py-2 px-4 border-b border-gray-200 text-sm">${item.transactionFees.toFixed(2)}</td>
                        <td className="py-2 px-4 border-b border-gray-200 text-sm">${item.subscriptionFees.toFixed(2)}</td>
                        <td className="py-2 px-4 border-b border-gray-200 text-sm">${item.profitShare.toFixed(2)}</td>
                        <td className="py-2 px-4 border-b border-gray-200 text-sm">${item.total.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Subscriptions Tab */}
          {activeTab === 'subscriptions' && (
            <div className="bg-white p-4 rounded-lg shadow-md overflow-x-auto">
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <FontAwesomeIcon icon={faClipboardList} /> Active Subscriptions
              </h3>
              <table className="min-w-full bg-white">
                <thead className="bg-gray-200 text-gray-700">
                  <tr>
                    <th className="py-2 px-4 text-left text-sm font-semibold">Subscription Name</th>
                    <th className="py-2 px-4 text-left text-sm font-semibold">Monthly Amount (USD)</th>
                    <th className="py-2 px-4 text-left text-sm font-semibold">Start Date</th>
                    <th className="py-2 px-4 text-left text-sm font-semibold">End Date</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptionData.map((subscription, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="py-2 px-4 border-b border-gray-200 text-sm">{subscription.SubscriptionName}</td>
                      <td className="py-2 px-4 border-b border-gray-200 text-sm">${subscription.MonthlyAmount.toFixed(2)}</td>
                      <td className="py-2 px-4 border-b border-gray-200 text-sm">{format(toZonedTime(new Date(subscription.StartDate), 'America/Chicago'), 'MMM dd, yyyy')}</td>
                      <td className="py-2 px-4 border-b border-gray-200 text-sm">
                        {subscription.EndDate ? format(toZonedTime(new Date(subscription.EndDate), 'America/Chicago'), 'MMM dd, yyyy') : 'Ongoing'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Commissions Tab */}
          {activeTab === 'commissions' && (
            <div className="bg-white p-4 rounded-lg shadow-md overflow-x-auto">
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <FontAwesomeIcon icon={faFileInvoice} /> Commissions by Policy
              </h3>
              <p className="text-sm text-gray-500 mb-4">Total amount owed to the partnership</p>
              <table className="min-w-full bg-white">
                <thead className="bg-gray-200 text-gray-700">
                  <tr>
                    <th className="py-2 px-4 text-left text-sm font-semibold">Policy</th>
                    <th className="py-2 px-4 text-left text-sm font-semibold">Month</th>
                    <th className="py-2 px-4 text-left text-sm font-semibold">Total Commission (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {commissionData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="py-2 px-4 border-b border-gray-200 text-sm">{commissionPolicyMapping[item.policyNumber]}</td>
                      <td className="py-2 px-4 border-b border-gray-200 text-sm">{formatMonthYear(item.Month)}</td>
                      <td className="py-2 px-4 border-b border-gray-200 text-sm">${item.TotalCommission.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="bg-white p-4 rounded-lg shadow-md overflow-x-auto">
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <FontAwesomeIcon icon={faChartPie} /> Profit by Month
              </h3>
              <table className="min-w-full bg-white">
                <thead className="bg-gray-200 text-gray-700">
                  <tr>
                    <th className="py-2 px-4 text-left text-sm font-semibold">Month</th>
                    <th className="py-2 px-4 text-left text-sm font-semibold">Contract</th>
                    <th className="py-2 px-4 text-left text-sm font-semibold">Total Profit (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {profitData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="py-2 px-4 border-b border-gray-200 text-sm">{formatMonthYear(item.Month)}</td>
                      <td className="py-2 px-4 border-b border-gray-200 text-sm">{getContractDescription(item.ContractId)}</td>
                      <td className="py-2 px-4 border-b border-gray-200 text-sm">${item.TotalProfit.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {alertMessage && <p className="text-red-500 mt-4">{alertMessage}</p>}
    </div>
  );
};

export default UsageStats;


// import React, { useState, useEffect } from 'react';
// import {
//   LineChart,
//   Line,
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   Legend,
//   ResponsiveContainer,
// } from 'recharts';
// import { useAuth } from './AuthContext';
// import { format, parse } from 'date-fns';
// import Spinner from './Spinner';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faChartLine, faDollarSign, faClipboardList, faChartBar } from '@fortawesome/free-solid-svg-icons';

// // -- Configuration for policies and contracts -- //
// const policyDeductionConfig = [
//   // e.g., policyNumber = 334 => Deduct 100% (1.0) of that policy's commission from profit
//   { policyNumber: 334, deductionPercentage: 1.0 },
//   // Add more policies or change deductionPercentage as needed
// ];

// const contractProfitSplit = [
//   // For contract 745687 (Fuellink), we take 50% (0.5) of the remaining profit after commissions
//   { contractId: 745687, splitPercentage: 0.5 },
//   // Add more contracts or adjust splitPercentage as needed
// ];

// // -- Contract Mapping -- //
// const contractMapping = [
//   { contractId: 273898, description: 'GO TRANSPORT LLC' },
//   { contractId: 392261, description: 'eCapital' },
//   { contractId: 425680, description: 'C Fuel' },
//   { contractId: 492191, description: 'BG' },
//   { contractId: 684747, description: 'FBT' },
//   { contractId: 684748, description: 'Fila Mar' },
//   { contractId: 745687, description: 'Fuellink' },
//   { contractId: 753698, description: 'AQHA' },
//   { contractId: 793342, description: 'CANADIAN' },
// ];

// // -- Utility Functions -- //
// const formatMonthYear = (dateString) => {
//   const date = parse(`${dateString}-01`, 'yyyy-MM-dd', new Date());
//   return format(date, 'MMM-yyyy');
// };

// const getContractDescription = (contractId) => {
//   const contract = contractMapping.find((item) => item.contractId === contractId);
//   return contract ? contract.description : contractId;
// };

// const aggregateDataByMonth = (data) => {
//   const aggregatedData = {};
//   data.forEach((item) => {
//     const key = item.month;
//     if (!aggregatedData[key]) {
//       aggregatedData[key] = {
//         month: item.month,
//         transactionCount: item.transactionCount,
//         totalAmount: item.totalAmount,
//       };
//     } else {
//       aggregatedData[key].transactionCount += item.transactionCount;
//       aggregatedData[key].totalAmount += item.totalAmount;
//     }
//   });
//   return Object.values(aggregatedData);
// };

// // -- Updated computeBillingData to handle commissions and profit-sharing -- //
// function computeBillingData(usageData, subscriptionData, profitData, commissionData) {
//   const profitMap = {};
//   profitData.forEach((p) => {
//     const key = `${p.Month}-${p.ContractId}`;
//     profitMap[key] = p.TotalProfit;
//   });

//   const commissionMap = {};
//   commissionData.forEach((c) => {
//     const key = `${c.Month}-${c.policyNumber}`;
//     commissionMap[key] = c.TotalCommission;
//   });

//   const billingDataMap = {};

//   // Populate billingDataMap from usageData
//   usageData.forEach((item) => {
//     const { monthKey, month, contractId, transactionCount } = item;

//     if (!billingDataMap[monthKey]) {
//       billingDataMap[monthKey] = {
//         monthKey, // Add monthKey here
//         month,
//         totalTransactionCount: 0,
//         transactionFees: 0.0,
//         subscriptionFees: 0.0,
//         billingAmount: 0.0,
//         profitShare: 0.0,
//       };
//     }

//     if (contractId !== 273898) {
//       billingDataMap[monthKey].totalTransactionCount += transactionCount;
//     }
//   });

//   // Add subscription fees per month
//   subscriptionData.forEach((subscription) => {
//     const { MonthlyAmount, StartDate, EndDate } = subscription;
//     const start = new Date(StartDate);
//     const end = EndDate ? new Date(EndDate) : new Date();

//     let currentMonth = new Date(start.getFullYear(), start.getMonth(), 1);
//     let endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
//     endMonth.setMonth(endMonth.getMonth() + 1);

//     while (currentMonth < endMonth) {
//       const key = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;

//       if (!billingDataMap[key]) {
//         billingDataMap[key] = {
//           monthKey: key, // Add monthKey here
//           month: formatMonthYear(key),
//           totalTransactionCount: 0,
//           transactionFees: 0.0,
//           subscriptionFees: 0.0,
//           billingAmount: 0.0,
//           profitShare: 0.0,
//         };
//       }

//       billingDataMap[key].subscriptionFees += MonthlyAmount;
//       currentMonth.setMonth(currentMonth.getMonth() + 1);
//     }
//   });

//   // Calculate transaction fees
//   Object.values(billingDataMap).forEach((item) => {
//     const transactionCount = item.totalTransactionCount;
//     let transactionFee = 0.0;
//     if (transactionCount > 100) {
//       transactionFee = Math.ceil((transactionCount - 100) / 100) * 50.0;
//     }
//     item.transactionFees = transactionFee;
//   });

//   // Process profit data to add profit-sharing
//   profitData.forEach((p) => {
//     const monthKey = p.Month;
//     const contractId = p.ContractId;
//     if (!billingDataMap[monthKey]) return;

//     let remainingProfit = p.TotalProfit || 0.0;

//     policyDeductionConfig.forEach((policyCfg) => {
//       const commissionKey = `${monthKey}-${policyCfg.policyNumber}`;
//       if (commissionMap[commissionKey]) {
//         const commissionAmt = commissionMap[commissionKey];
//         remainingProfit -= commissionAmt * policyCfg.deductionPercentage;
//       }
//     });

//     const splitCfg = contractProfitSplit.find((c) => c.contractId === contractId);
//     if (splitCfg) {
//       const share = remainingProfit * splitCfg.splitPercentage;
//       billingDataMap[monthKey].profitShare += share;
//     }
//   });

//   Object.values(billingDataMap).forEach((item) => {
//     item.billingAmount = item.transactionFees + item.subscriptionFees + item.profitShare;
//   });

//   return Object.keys(billingDataMap)
//     .sort()
//     .map((key) => billingDataMap[key]);
// }

// const UsageStats = () => {
//   const { currentUser } = useAuth();
//   const [startDate, setStartDate] = useState('');
//   const [endDate, setEndDate] = useState('');
//   const [selectedContract, setSelectedContract] = useState('all');
//   const [usageData, setUsageData] = useState([]);
//   const [aggregatedChartData, setAggregatedChartData] = useState([]);
//   const [billingData, setBillingData] = useState([]);
//   const [subscriptionData, setSubscriptionData] = useState([]);
//   const [profitData, setProfitData] = useState([]);
//   const [commissionData, setCommissionData] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [alertMessage, setAlertMessage] = useState(null);
//   const [activeTab, setActiveTab] = useState('usage');
//   const apiUrl = process.env.REACT_APP_API_URL;

//   // Function to check if a month is within the date range
//   const isWithinDateRange = (monthKey) => {
//     const [year, month] = monthKey.split('-').map(Number);
//     const itemDate = new Date(year, month - 1, 1);
//     const start = startDate ? new Date(startDate) : new Date(0); // Beginning of time
//     const end = endDate ? new Date(endDate) : new Date(); // Now
//     return itemDate >= start && itemDate <= end;
//   };

//   useEffect(() => {
//     const fetchData = async () => {
//       if (!currentUser?.userId) return;

//       setLoading(true);
//       try {
//         const response = await fetch(`${apiUrl}/dataServer`, {
//           method: 'POST',
//           headers: { 'Content-Type': 'application/json' },
//           body: JSON.stringify({
//             action: 'getMonthlyUsageData',
//             payload: { userId: currentUser?.userId },
//           }),
//         });

//         if (response.ok) {
//           const fetchedData = await response.json();
//           console.log('Fetched Data:', fetchedData); // Debugging line
//           const { monthlyUsageData, subscriptionData, profitData, commissionData } = fetchedData;

//           // Filter usage data by date and contract
//           const filteredUsageData = monthlyUsageData
//             .filter((item) => {
//               const itemDate = parse(`${item.Month}-01`, 'yyyy-MM-dd', new Date());
//               const startDateObj = startDate ? parse(startDate, 'yyyy-MM-dd', new Date()) : null;
//               const endDateObj = endDate ? parse(endDate, 'yyyy-MM-dd', new Date()) : null;
//               return (
//                 (!startDateObj || itemDate >= startDateObj) &&
//                 (!endDateObj || itemDate <= endDateObj) &&
//                 (selectedContract === 'all' || item.ContractId === parseInt(selectedContract))
//               );
//             })
//             .map((item) => ({
//               monthKey: item.Month,
//               month: formatMonthYear(item.Month),
//               contract: getContractDescription(item.ContractId),
//               contractId: item.ContractId,
//               transactionCount: item.TransactionCount,
//               totalAmount: item.TotalAmount,
//             }));

//           // Prepare unfiltered usage data for billing computation
//           const unfilteredUsageData = monthlyUsageData.map((item) => ({
//             monthKey: item.Month,
//             month: formatMonthYear(item.Month),
//             contractId: item.ContractId,
//             transactionCount: item.TransactionCount,
//           }));

//           // Compute billing data with full data
//           const computedBillingData = computeBillingData(
//             unfilteredUsageData,
//             subscriptionData,
//             profitData,
//             commissionData
//           );

//           // Filter billing, profit, and commission data by date range
//           const filteredBillingData = computedBillingData.filter((item) =>
//             isWithinDateRange(item.monthKey)
//           );
//           const filteredProfitData = profitData.filter((item) => isWithinDateRange(item.Month));
//           const filteredCommissionData = commissionData.filter((item) =>
//             isWithinDateRange(item.Month)
//           );

//           // Set state
//           setUsageData(filteredUsageData);
//           setAggregatedChartData(aggregateDataByMonth(filteredUsageData));
//           setBillingData(filteredBillingData);
//           setSubscriptionData(subscriptionData); // Subscriptions remain unfiltered
//           setProfitData(filteredProfitData);
//           setCommissionData(filteredCommissionData);
//         } else {
//           setAlertMessage(`Server Error: ${await response.text()}`);
//         }
//       } catch (error) {
//         setAlertMessage(`Error: ${error.message}`);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, [currentUser?.userId, startDate, endDate, selectedContract, apiUrl]);

//   return (
//     <div className="p-6 bg-gray-100 min-h-screen">
//       <h2 className="text-2xl font-bold mb-6">Usage Statistics</h2>

//       {/* Filters */}
//       <div className="mb-6 flex gap-4 flex-wrap">
//         <div className="flex items-center gap-2">
//           <label className="font-semibold">Start Date:</label>
//           <input
//             type="date"
//             value={startDate}
//             onChange={(e) => setStartDate(e.target.value)}
//             className="border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
//           />
//         </div>
//         <div className="flex items-center gap-2">
//           <label className="font-semibold">End Date:</label>
//           <input
//             type="date"
//             value={endDate}
//             onChange={(e) => setEndDate(e.target.value)}
//             className="border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
//           />
//         </div>
//         <div className="flex items-center gap-2">
//           <label className="font-semibold">Contract:</label>
//           <select
//             value={selectedContract}
//             onChange={(e) => setSelectedContract(e.target.value)}
//             className="border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
//           >
//             <option value="all">All</option>
//             {contractMapping.map((contract) => (
//               <option key={contract.contractId} value={contract.contractId}>
//                 {contract.description}
//               </option>
//             ))}
//           </select>
//         </div>
//       </div>

//       {loading ? (
//         <Spinner />
//       ) : (
//         <>
//           {/* Tab Headers */}
//           <div className="flex gap-4 mb-6">
//             <button
//               className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
//                 activeTab === 'usage'
//                   ? 'bg-gradient-to-r from-[var(--primary-color)] to-[var(--secondary-color)] text-white'
//                   : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
//               }`}
//               onClick={() => setActiveTab('usage')}
//             >
//               <FontAwesomeIcon icon={faChartLine} /> Usage
//             </button>
//             <button
//               className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
//                 activeTab === 'billing'
//                   ? 'bg-gradient-to-r from-[var(--primary-color)] to-[var(--secondary-color)] text-white'
//                   : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
//               }`}
//               onClick={() => setActiveTab('billing')}
//             >
//               <FontAwesomeIcon icon={faDollarSign} /> Billing
//             </button>
//             <button
//               className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
//                 activeTab === 'subscriptions'
//                   ? 'bg-gradient-to-r from-[var(--primary-color)] to-[var(--secondary-color)] text-white'
//                   : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
//               }`}
//               onClick={() => setActiveTab('subscriptions')}
//             >
//               <FontAwesomeIcon icon={faClipboardList} /> Subscriptions
//             </button>
//           </div>

//           {/* Tab Content */}
//           {activeTab === 'usage' && (
//             <div>
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
//                 {/* Line Chart */}
//                 <div className="bg-white p-4 rounded-lg shadow-md">
//                   <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
//                     <FontAwesomeIcon icon={faChartLine} /> Monthly Usage (Line Chart)
//                   </h3>
//                   <ResponsiveContainer width="100%" height={300}>
//                     <LineChart data={aggregatedChartData}>
//                       <CartesianGrid strokeDasharray="3 3" />
//                       <XAxis dataKey="month" />
//                       <YAxis />
//                       <Tooltip formatter={(value) => new Intl.NumberFormat('en').format(value)} />
//                       <Legend />
//                       <Line
//                         type="monotone"
//                         dataKey="totalAmount"
//                         stroke="var(--primary-color)"
//                         name="Total Amount"
//                         yAxisId={0}
//                       />
//                       <Line
//                         type="monotone"
//                         dataKey="transactionCount"
//                         stroke="var(--secondary-color)"
//                         name="Transaction Count"
//                         yAxisId={1}
//                       />
//                       <YAxis yAxisId={0} orientation="left" stroke="var(--primary-color)" />
//                       <YAxis yAxisId={1} orientation="right" stroke="var(--secondary-color)" />
//                     </LineChart>
//                   </ResponsiveContainer>
//                 </div>
//                 {/* Bar Chart */}
//                 <div className="bg-white p-4 rounded-lg shadow-md">
//                   <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
//                     <FontAwesomeIcon icon={faChartBar} /> Monthly Usage (Bar Chart)
//                   </h3>
//                   <ResponsiveContainer width="100%" height={300}>
//                     <BarChart data={aggregatedChartData}>
//                       <CartesianGrid strokeDasharray="3 3" />
//                       <XAxis dataKey="month" />
//                       <YAxis />
//                       <Tooltip formatter={(value) => new Intl.NumberFormat('en').format(value)} />
//                       <Legend />
//                       <Bar
//                         dataKey="totalAmount"
//                         fill="var(--primary-color)"
//                         name="Total Amount"
//                         yAxisId={0}
//                       />
//                       <Bar
//                         dataKey="transactionCount"
//                         fill="var(--secondary-color)"
//                         name="Transaction Count"
//                         yAxisId={1}
//                       />
//                       <YAxis yAxisId={0} orientation="left" stroke="var(--primary-color)" />
//                       <YAxis yAxisId={1} orientation="right" stroke="var(--secondary-color)" />
//                     </BarChart>
//                   </ResponsiveContainer>
//                 </div>
//               </div>
//               {/* Detailed Usage Table */}
//               <div className="bg-white p-4 rounded-lg shadow-md overflow-x-auto">
//                 <h3 className="text-xl font-semibold mb-4">Detailed Monthly Usage</h3>
//                 <table className="min-w-full bg-white">
//                   <thead className="bg-gray-200 text-gray-700">
//                     <tr>
//                       <th className="py-2 px-4 text-left text-sm font-semibold">Month</th>
//                       <th className="py-2 px-4 text-left text-sm font-semibold">Contract</th>
//                       <th className="py-2 px-4 text-left text-sm font-semibold">Transaction Count</th>
//                       <th className="py-2 px-4 text-left text-sm font-semibold">Total Amount (USD)</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {usageData.map((item, index) => (
//                       <tr key={index} className="hover:bg-gray-50">
//                         <td className="py-2 px-4 border-b border-gray-200 text-sm">{item.month}</td>
//                         <td className="py-2 px-4 border-b border-gray-200 text-sm">{item.contract}</td>
//                         <td className="py-2 px-4 border-b border-gray-200 text-sm">
//                           {item.transactionCount}
//                         </td>
//                         <td className="py-2 px-4 border-b border-gray-200 text-sm">
//                           ${item.totalAmount.toFixed(2)}
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             </div>
//           )}

//           {activeTab === 'billing' && (
//             <div>
//               {/* Billing Chart */}
//               <div className="bg-white p-4 rounded-lg shadow-md mb-6">
//                 <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
//                   <FontAwesomeIcon icon={faDollarSign} /> Monthly Billing
//                 </h3>
//                 <ResponsiveContainer width="100%" height={300}>
//                   <LineChart data={billingData}>
//                     <CartesianGrid strokeDasharray="3 3" />
//                     <XAxis dataKey="month" />
//                     <YAxis />
//                     <Tooltip
//                       formatter={(value) => `$${new Intl.NumberFormat('en').format(value.toFixed(2))}`}
//                     />
//                     <Legend />
//                     <Line
//                       type="monotone"
//                       dataKey="billingAmount"
//                       stroke="var(--primary-color)"
//                       name="Total Billing Amount"
//                     />
//                   </LineChart>
//                 </ResponsiveContainer>
//               </div>
//               {/* Billing Table */}
//               <div className="bg-white p-4 rounded-lg shadow-md mb-6 overflow-x-auto">
//                 <h3 className="text-xl font-semibold mb-4">Billing Details</h3>
//                 <table className="min-w-full bg-white">
//                   <thead className="bg-gray-200 text-gray-700">
//                     <tr>
//                       <th className="py-2 px-4 text-left text-sm font-semibold">Month</th>
//                       <th className="py-2 px-4 text-left text-sm font-semibold">
//                         Total Transaction Count
//                       </th>
//                       <th className="py-2 px-4 text-left text-sm font-semibold">Transaction Fees (USD)</th>
//                       <th className="py-2 px-4 text-left text-sm font-semibold">Subscription Fees (USD)</th>
//                       <th className="py-2 px-4 text-left text-sm font-semibold">Profit Share (USD)</th>
//                       <th className="py-2 px-4 text-left text-sm font-semibold">Billing Amount (USD)</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {billingData.map((item, index) => (
//                       <tr key={index} className="hover:bg-gray-50">
//                         <td className="py-2 px-4 border-b border-gray-200 text-sm">{item.month}</td>
//                         <td className="py-2 px-4 border-b border-gray-200 text-sm">
//                           {item.totalTransactionCount}
//                         </td>
//                         <td className="py-2 px-4 border-b border-gray-200 text-sm">
//                           ${item.transactionFees.toFixed(2)}
//                         </td>
//                         <td className="py-2 px-4 border-b border-gray-200 text-sm">
//                           ${item.subscriptionFees.toFixed(2)}
//                         </td>
//                         <td className="py-2 px-4 border-b border-gray-200 text-sm">
//                           ${item.profitShare?.toFixed(2) || '0.00'}
//                         </td>
//                         <td className="py-2 px-4 border-b border-gray-200 text-sm">
//                           ${item.billingAmount.toFixed(2)}
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//               {/* Profit Table */}
//               <div className="bg-white p-4 rounded-lg shadow-md mb-6 overflow-x-auto">
//                 <h3 className="text-xl font-semibold mb-4">Profit by Month</h3>
//                 <table className="min-w-full bg-white">
//                   <thead className="bg-gray-200 text-gray-700">
//                     <tr>
//                       <th className="py-2 px-4 text-left text-sm font-semibold">Month</th>
//                       <th className="py-2 px-4 text-left text-sm font-semibold">Contract</th>
//                       <th className="py-2 px-4 text-left text-sm font-semibold">Total Profit (USD)</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {profitData.map((item, index) => (
//                       <tr key={index} className="hover:bg-gray-50">
//                         <td className="py-2 px-4 border-b border-gray-200 text-sm">
//                           {formatMonthYear(item.Month)}
//                         </td>
//                         <td className="py-2 px-4 border-b border-gray-200 text-sm">
//                           {getContractDescription(item.ContractId)}
//                         </td>
//                         <td className="py-2 px-4 border-b border-gray-200 text-sm">
//                           ${item.TotalProfit.toFixed(2)}
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//               {/* Commission Table */}
//               <div className="bg-white p-4 rounded-lg shadow-md overflow-x-auto">
//                 <h3 className="text-xl font-semibold mb-4">Commission by Policy</h3>
//                 <p className="text-sm text-gray-500 mb-4">Total amount owed to the partnership</p>
//                 <table className="min-w-full bg-white">
//                   <thead className="bg-gray-200 text-gray-700">
//                     <tr>
//                       <th className="py-2 px-4 text-left text-sm font-semibold">Policy</th>
//                       <th className="py-2 px-4 text-left text-sm font-semibold">Month</th>
//                       <th className="py-2 px-4 text-left text-sm font-semibold">Total Commission (USD)</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {commissionData.map((item, index) => (
//                       <tr key={index} className="hover:bg-gray-50">
//                         <td className="py-2 px-4 border-b border-gray-200 text-sm">
//                           {item.policyNumber}
//                         </td>
//                         <td className="py-2 px-4 border-b border-gray-200 text-sm">
//                           {formatMonthYear(item.Month)}
//                         </td>
//                         <td className="py-2 px-4 border-b border-gray-200 text-sm">
//                           ${item.TotalCommission.toFixed(2)}
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             </div>
//           )}

//           {activeTab === 'subscriptions' && (
//             <div className="bg-white p-4 rounded-lg shadow-md overflow-x-auto">
//               <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
//                 <FontAwesomeIcon icon={faClipboardList} /> Active Subscriptions
//               </h3>
//               <table className="min-w-full bg-white">
//                 <thead className="bg-gray-200 text-gray-700">
//                   <tr>
//                     <th className="py-2 px-4 text-left text-sm font-semibold">Subscription Name</th>
//                     <th className="py-2 px-4 text-left text-sm font-semibold">Monthly Amount (USD)</th>
//                     <th className="py-2 px-4 text-left text-sm font-semibold">Start Date</th>
//                     <th className="py-2 px-4 text-left text-sm font-semibold">End Date</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {subscriptionData.map((subscription, index) => (
//                     <tr key={index} className="hover:bg-gray-50">
//                       <td className="py-2 px-4 border-b border-gray-200 text-sm">
//                         {subscription.SubscriptionName}
//                       </td>
//                       <td className="py-2 px-4 border-b border-gray-200 text-sm">
//                         ${subscription.MonthlyAmount.toFixed(2)}
//                       </td>
//                       <td className="py-2 px-4 border-b border-gray-200 text-sm">
//                         {format(new Date(subscription.StartDate), 'MMM dd, yyyy')}
//                       </td>
//                       <td className="py-2 px-4 border-b border-gray-200 text-sm">
//                         {subscription.EndDate
//                           ? format(new Date(subscription.EndDate), 'MMM dd, yyyy')
//                           : 'Ongoing'}
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           )}
//         </>
//       )}

//       {alertMessage && <p className="text-red-500 mt-4">{alertMessage}</p>}
//     </div>
//   );
// };

// export default UsageStats;



// // import React, { useState, useEffect } from 'react';
// // import {
// //   LineChart,
// //   Line,
// //   BarChart,
// //   Bar,
// //   XAxis,
// //   YAxis,
// //   CartesianGrid,
// //   Tooltip,
// //   Legend,
// //   ResponsiveContainer,
// // } from 'recharts';
// // import { useAuth } from './AuthContext';
// // import { format, parse } from 'date-fns';
// // import Spinner from './Spinner';

// // // -- Configuration for policies and contracts -- //
// // const policyDeductionConfig = [
// //   // e.g., policyNumber = 334 => Deduct 100% (1.0) of that policy's commission from profit
// //   { policyNumber: 334, deductionPercentage: 1.0 },
// //   // Add more policies or change deductionPercentage as needed
// // ];

// // const contractProfitSplit = [
// //   // For contract 745687 (Fuellink), we take 50% (0.5) of the remaining profit after commissions
// //   { contractId: 745687, splitPercentage: 0.5 },
// //   // Add more contracts or adjust splitPercentage as needed
// // ];

// // // -- Contract Mapping -- //
// // const contractMapping = [
// //   { contractId: 273898, description: 'GO TRANSPORT LLC' },
// //   { contractId: 392261, description: 'eCapital' },
// //   { contractId: 425680, description: 'C Fuel' },
// //   { contractId: 492191, description: 'BG' },
// //   { contractId: 684747, description: 'FBT' },
// //   { contractId: 684748, description: 'Fila Mar' },
// //   { contractId: 745687, description: 'Fuellink' },
// //   { contractId: 753698, description: 'AQHA' },
// //   { contractId: 793342, description: 'CANADIAN' },
// // ];

// // // -- Utility Functions -- //
// // const formatMonthYear = (dateString) => {
// //   const date = parse(`${dateString}-01`, 'yyyy-MM-dd', new Date());
// //   return format(date, 'MMM-yyyy');
// // };

// // const getContractDescription = (contractId) => {
// //   const contract = contractMapping.find((item) => item.contractId === contractId);
// //   return contract ? contract.description : contractId;
// // };

// // const aggregateDataByMonth = (data) => {
// //   const aggregatedData = {};
// //   data.forEach((item) => {
// //     const key = item.month;
// //     if (!aggregatedData[key]) {
// //       aggregatedData[key] = {
// //         month: item.month,
// //         transactionCount: item.transactionCount,
// //         totalAmount: item.totalAmount,
// //       };
// //     } else {
// //       aggregatedData[key].transactionCount += item.transactionCount;
// //       aggregatedData[key].totalAmount += item.totalAmount;
// //     }
// //   });
// //   return Object.values(aggregatedData);
// // };

// // // -- Updated computeBillingData to handle commissions and profit-sharing -- //
// // function computeBillingData(usageData, subscriptionData, profitData, commissionData) {
// //   // Build quick lookups for profit & commission
// //   const profitMap = {};
// //   profitData.forEach((p) => {
// //     const key = `${p.Month}-${p.ContractId}`;
// //     profitMap[key] = p.TotalProfit;
// //   });

// //   const commissionMap = {};
// //   commissionData.forEach((c) => {
// //     const key = `${c.Month}-${c.policyNumber}`;
// //     commissionMap[key] = c.TotalCommission;
// //   });

// //   const billingDataMap = {};

// //   // Populate billingDataMap from usageData
// //   usageData.forEach((item) => {
// //     const { monthKey, month, contractId, transactionCount } = item;

// //     if (!billingDataMap[monthKey]) {
// //       billingDataMap[monthKey] = {
// //         month,
// //         totalTransactionCount: 0,
// //         transactionFees: 0.0,
// //         subscriptionFees: 0.0,
// //         billingAmount: 0.0,
// //         profitShare: 0.0, // We'll accumulate additional revenue from profit splits here
// //       };
// //     }

// //     // Exclude ContractId 273898 from transaction counts
// //     if (contractId !== 273898) {
// //       billingDataMap[monthKey].totalTransactionCount += transactionCount;
// //     }
// //   });

// //   // Add subscription fees per month
// //   subscriptionData.forEach((subscription) => {
// //     const { MonthlyAmount, StartDate, EndDate } = subscription;
// //     const start = new Date(StartDate);
// //     const end = EndDate ? new Date(EndDate) : new Date();

// //     let currentMonth = new Date(start.getFullYear(), start.getMonth(), 1);
// //     let endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
// //     endMonth.setMonth(endMonth.getMonth() + 1); // move to the start of the following month

// //     while (currentMonth < endMonth) {
// //       const key = `${currentMonth.getFullYear()}-${String(
// //         currentMonth.getMonth() + 1
// //       ).padStart(2, '0')}`;

// //       if (!billingDataMap[key]) {
// //         billingDataMap[key] = {
// //           month: formatMonthYear(key),
// //           totalTransactionCount: 0,
// //           transactionFees: 0.0,
// //           subscriptionFees: 0.0,
// //           billingAmount: 0.0,
// //           profitShare: 0.0,
// //         };
// //       }

// //       billingDataMap[key].subscriptionFees += MonthlyAmount;
// //       currentMonth.setMonth(currentMonth.getMonth() + 1);
// //     }
// //   });

// //   // Calculate transaction fees
// //   Object.values(billingDataMap).forEach((item) => {
// //     const transactionCount = item.totalTransactionCount;
// //     let transactionFee = 0.0;
// //     if (transactionCount > 100) {
// //       transactionFee = Math.ceil((transactionCount - 100) / 100) * 50.0;
// //     }
// //     item.transactionFees = transactionFee;
// //   });

// //   // Process profit data to add profit-sharing
// //   profitData.forEach((p) => {
// //     const monthKey = p.Month;
// //     const contractId = p.ContractId;
// //     if (!billingDataMap[monthKey]) return;

// //     let remainingProfit = p.TotalProfit || 0.0;

// //     // Deduct relevant policy commissions
// //     policyDeductionConfig.forEach((policyCfg) => {
// //       const commissionKey = `${monthKey}-${policyCfg.policyNumber}`;
// //       if (commissionMap[commissionKey]) {
// //         // e.g. policy 334 => deduct 100% of that commission from the profit
// //         const commissionAmt = commissionMap[commissionKey];
// //         remainingProfit -= commissionAmt * policyCfg.deductionPercentage;
// //       }
// //     });

// //     // Check if the contract requires a profit split
// //     const splitCfg = contractProfitSplit.find((c) => c.contractId === contractId);
// //     if (splitCfg) {
// //       const share = remainingProfit * splitCfg.splitPercentage;
// //       billingDataMap[monthKey].profitShare += share;
// //     }
// //   });

// //   // Final billing amount = transactionFees + subscriptionFees + profitShare
// //   Object.values(billingDataMap).forEach((item) => {
// //     item.billingAmount = item.transactionFees + item.subscriptionFees + item.profitShare;
// //   });

// //   // Sort by YYYY-MM key
// //   return Object.keys(billingDataMap)
// //     .sort()
// //     .map((key) => billingDataMap[key]);
// // }

// // const UsageStats = () => {
// //   const { currentUser } = useAuth();
// //   const [startDate, setStartDate] = useState('');
// //   const [endDate, setEndDate] = useState('');
// //   const [selectedContract, setSelectedContract] = useState('all');
// //   const [usageData, setUsageData] = useState([]);
// //   const [aggregatedChartData, setAggregatedChartData] = useState([]);
// //   const [billingData, setBillingData] = useState([]);
// //   const [subscriptionData, setSubscriptionData] = useState([]);
// //   const [loading, setLoading] = useState(false);
// //   const [alertMessage, setAlertMessage] = useState(null);
// //   const [profitData, setProfitData] = useState([]);
// //   const [commissionData, setCommissionData] = useState([]);
// //   const apiUrl = process.env.REACT_APP_API_URL;

// //   useEffect(() => {
// //     const fetchData = async () => {
// //       if (!currentUser?.userId) return;

// //       setLoading(true);
// //       try {
// //         const response = await fetch(`${apiUrl}/dataServer`, {
// //           method: 'POST',
// //           headers: { 'Content-Type': 'application/json' },
// //           body: JSON.stringify({
// //             action: 'getMonthlyUsageData',
// //             payload: {
// //               userId: currentUser?.userId,
// //             },
// //           }),
// //         });

// //         if (response.ok) {
// //           const fetchedData = await response.json();
// //           console.log('Fetched data:', fetchedData);

// //           const { monthlyUsageData, subscriptionData, profitData, commissionData } = fetchedData;

// //           // Parse date filters
// //           const startDateObj = startDate ? parse(startDate, 'yyyy-MM-dd', new Date()) : null;
// //           const endDateObj = endDate ? parse(endDate, 'yyyy-MM-dd', new Date()) : null;

// //           // Filter usage data for display
// //           const filteredData = monthlyUsageData
// //             .filter((item) => {
// //               const itemDate = parse(`${item.Month}-01`, 'yyyy-MM-dd', new Date());
// //               return (
// //                 (!startDateObj || itemDate >= startDateObj) &&
// //                 (!endDateObj || itemDate <= endDateObj) &&
// //                 (selectedContract === 'all' || item.ContractId === parseInt(selectedContract))
// //               );
// //             })
// //             .map((item) => {
// //               const monthKey = item.Month; // 'YYYY-MM'
// //               return {
// //                 monthKey,
// //                 month: formatMonthYear(monthKey),
// //                 contract: getContractDescription(item.ContractId),
// //                 contractId: item.ContractId,
// //                 transactionCount: item.TransactionCount,
// //                 totalAmount: item.TotalAmount,
// //               };
// //             });

// //           setCommissionData(commissionData);
// //           setProfitData(profitData);
// //           setUsageData(filteredData);
// //           setSubscriptionData(subscriptionData);
// //           setAggregatedChartData(aggregateDataByMonth(filteredData));

// //           // Prepare unfiltered usage data for billing computation
// //           const unfilteredUsageData = monthlyUsageData.map((item) => {
// //             const monthKey = item.Month; // 'YYYY-MM'
// //             return {
// //               monthKey,
// //               month: formatMonthYear(monthKey),
// //               contractId: item.ContractId,
// //               transactionCount: item.TransactionCount,
// //             };
// //           });

// //           // Compute billing data (now including commission & profit share)
// //           const computedBillingData = computeBillingData(
// //             unfilteredUsageData,
// //             subscriptionData,
// //             profitData,
// //             commissionData
// //           );
// //           setBillingData(computedBillingData);
// //         } else {
// //           setAlertMessage(`Server Error: ${await response.text()}`);
// //         }
// //       } catch (error) {
// //         setAlertMessage(`Error: ${error.message}`);
// //       } finally {
// //         setLoading(false);
// //       }
// //     };

// //     fetchData();
// //   }, [currentUser?.userId, startDate, endDate, selectedContract, apiUrl]);

// //   return (
// //     <div className="p-6 bg-gray-100">
// //       <h2 className="text-2xl font-bold mb-4">Usage Statistics</h2>

// //       <div className="mb-6 flex gap-4 flex-wrap">
// //         {/* Date Filters */}
// //         <div className="flex items-center gap-2">
// //           <label className="font-semibold">Start Date:</label>
// //           <input
// //             type="date"
// //             value={startDate}
// //             onChange={(e) => setStartDate(e.target.value)}
// //             className="border p-2 rounded-lg"
// //           />
// //         </div>
// //         <div className="flex items-center gap-2">
// //           <label className="font-semibold">End Date:</label>
// //           <input
// //             type="date"
// //             value={endDate}
// //             onChange={(e) => setEndDate(e.target.value)}
// //             className="border p-2 rounded-lg"
// //           />
// //         </div>

// //         {/* Contract Filter */}
// //         <div className="flex items-center gap-2">
// //           <label className="font-semibold">Contract:</label>
// //           <select
// //             value={selectedContract}
// //             onChange={(e) => setSelectedContract(e.target.value)}
// //             className="border p-2 rounded-lg"
// //           >
// //             <option value="all">All</option>
// //             {contractMapping.map((contract) => (
// //               <option key={contract.contractId} value={contract.contractId}>
// //                 {contract.description}
// //               </option>
// //             ))}
// //           </select>
// //         </div>
// //       </div>

// //       {loading ? (
// //         <Spinner />
// //       ) : (
// //         <>
// //           {/* Monthly Usage Charts */}
// //           <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
// //             {/* Line Chart */}
// //             <div className="bg-white p-4 rounded-lg shadow-md">
// //               <h3 className="text-xl font-semibold mb-4">Monthly Usage (Line Chart)</h3>
// //               <ResponsiveContainer width="100%" height={300}>
// //                 <LineChart data={aggregatedChartData}>
// //                   <CartesianGrid strokeDasharray="3 3" />
// //                   <XAxis dataKey="month" />
// //                   <YAxis />
// //                   <Tooltip formatter={(value) => new Intl.NumberFormat('en').format(value)} />
// //                   <Legend />
// //                   <Line
// //                     type="monotone"
// //                     dataKey="totalAmount"
// //                     stroke="#8884d8"
// //                     name="Total Amount"
// //                     yAxisId={0}
// //                   />
// //                   <Line
// //                     type="monotone"
// //                     dataKey="transactionCount"
// //                     stroke="#82ca9d"
// //                     name="Transaction Count"
// //                     yAxisId={1}
// //                   />
// //                   <YAxis yAxisId={0} orientation="left" stroke="#8884d8" />
// //                   <YAxis yAxisId={1} orientation="right" stroke="#82ca9d" />
// //                 </LineChart>
// //               </ResponsiveContainer>
// //             </div>

// //             {/* Bar Chart */}
// //             <div className="bg-white p-4 rounded-lg shadow-md">
// //               <h3 className="text-xl font-semibold mb-4">Monthly Usage (Bar Chart)</h3>
// //               <ResponsiveContainer width="100%" height={300}>
// //                 <BarChart data={aggregatedChartData}>
// //                   <CartesianGrid strokeDasharray="3 3" />
// //                   <XAxis dataKey="month" />
// //                   <YAxis />
// //                   <Tooltip formatter={(value) => new Intl.NumberFormat('en').format(value)} />
// //                   <Legend />
// //                   <Bar dataKey="totalAmount" fill="#8884d8" name="Total Amount" yAxisId={0} />
// //                   <Bar dataKey="transactionCount" fill="#82ca9d" name="Transaction Count" yAxisId={1} />
// //                   <YAxis yAxisId={0} orientation="left" stroke="#8884d8" />
// //                   <YAxis yAxisId={1} orientation="right" stroke="#82ca9d" />
// //                 </BarChart>
// //               </ResponsiveContainer>
// //             </div>
// //           </div>

// //           {/* Detailed Monthly Usage Table */}
// //           <div className="bg-white p-4 rounded-lg shadow-md">
// //             <h3 className="text-xl font-semibold mb-4">Detailed Monthly Usage</h3>
// //             <table className="min-w-full bg-white">
// //               <thead>
// //                 <tr>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     Month
// //                   </th>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     Contract
// //                   </th>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     Transaction Count
// //                   </th>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     Total Amount (USD)
// //                   </th>
// //                 </tr>
// //               </thead>
// //               <tbody>
// //                 {usageData.map((item, index) => (
// //                   <tr key={index}>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">{item.month}</td>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">{item.contract}</td>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">
// //                       {item.transactionCount}
// //                     </td>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">
// //                       ${item.totalAmount.toFixed(2)}
// //                     </td>
// //                   </tr>
// //                 ))}
// //               </tbody>
// //             </table>
// //           </div>

// //           {/* Monthly Billing Section */}
// //           <div className="bg-white p-4 rounded-lg shadow-md mt-6">
// //             <h3 className="text-xl font-semibold mb-4">Monthly Billing</h3>

// //             {/* Billing Chart */}
// //             <ResponsiveContainer width="100%" height={300}>
// //               <LineChart data={billingData}>
// //                 <CartesianGrid strokeDasharray="3 3" />
// //                 <XAxis dataKey="month" />
// //                 <YAxis />
// //                 <Tooltip
// //                   formatter={(value) => `$${new Intl.NumberFormat('en').format(value.toFixed(2))}`}
// //                 />
// //                 <Legend />
// //                 <Line
// //                   type="monotone"
// //                   dataKey="billingAmount"
// //                   stroke="#FF8042"
// //                   name="Total Billing Amount (Including Subscriptions/Profit Splits)"
// //                 />
// //               </LineChart>
// //             </ResponsiveContainer>

// //             {/* Billing Table */}
// //             <table className="min-w-full bg-white mt-6">
// //               <thead>
// //                 <tr>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     Month
// //                   </th>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     Total Transaction Count (Excluding Contract 273898)
// //                   </th>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     Transaction Fees (USD)
// //                   </th>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     Subscription Fees (USD)
// //                   </th>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     Profit Share (USD)
// //                   </th>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     Billing Amount (USD)
// //                   </th>
// //                 </tr>
// //               </thead>
// //               <tbody>
// //                 {billingData.map((item, index) => (
// //                   <tr key={index}>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">{item.month}</td>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">
// //                       {item.totalTransactionCount}
// //                     </td>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">
// //                       ${item.transactionFees.toFixed(2)}
// //                     </td>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">
// //                       ${item.subscriptionFees.toFixed(2)}
// //                     </td>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">
// //                       ${item.profitShare?.toFixed(2) || '0.00'}
// //                     </td>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">
// //                       ${item.billingAmount.toFixed(2)}
// //                     </td>
// //                   </tr>
// //                 ))}
// //               </tbody>
// //             </table>
// //           </div>

// //           {/* Profit Table */}
// //           <div className="bg-white p-4 rounded-lg shadow-md mt-6">
// //             <h3 className="text-xl font-semibold mb-4">Profit by Month</h3>
// //             <table className="min-w-full bg-white">
// //               <thead>
// //                 <tr>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     Month
// //                   </th>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     Contract
// //                   </th>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     Total Profit (USD)
// //                   </th>
// //                 </tr>
// //               </thead>
// //               <tbody>
// //                 {profitData.map((item, index) => (
// //                   <tr key={index}>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">
// //                       {formatMonthYear(item.Month)}
// //                     </td>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">
// //                       {getContractDescription(item.ContractId)}
// //                     </td>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">
// //                       ${item.TotalProfit.toFixed(2)}
// //                     </td>
// //                   </tr>
// //                 ))}
// //               </tbody>
// //             </table>
// //           </div>

// //           {/* Commission Table */}
// //           <div className="bg-white p-4 rounded-lg shadow-md mt-6">
// //             <h3 className="text-xl font-semibold mb-4">Commission by Policy</h3>
// //             <p className="text-sm text-gray-500 mb-4">Total amount owed to the partnership</p>
// //             <table className="min-w-full bg-white">
// //               <thead>
// //                 <tr>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     Policy
// //                   </th>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     Month
// //                   </th>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     Total Commission (USD)
// //                   </th>
// //                 </tr>
// //               </thead>
// //               <tbody>
// //                 {commissionData.map((item, index) => (
// //                   <tr key={index}>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">
// //                       {item.policyNumber}
// //                     </td>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">
// //                       {formatMonthYear(item.Month)}
// //                     </td>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">
// //                       ${item.TotalCommission.toFixed(2)}
// //                     </td>
// //                   </tr>
// //                 ))}
// //               </tbody>
// //             </table>
// //           </div>

// //           {/* Subscriptions Table */}
// //           <div className="bg-white p-4 rounded-lg shadow-md mt-6">
// //             <h3 className="text-xl font-semibold mb-4">Active Subscriptions</h3>
// //             <table className="min-w-full bg-white">
// //               <thead>
// //                 <tr>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     Subscription Name
// //                   </th>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     Monthly Amount (USD)
// //                   </th>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     Start Date
// //                   </th>
// //                   <th className="py-2 px-4 border-b border-gray-200 text-left text-sm font-semibold">
// //                     End Date
// //                   </th>
// //                 </tr>
// //               </thead>
// //               <tbody>
// //                 {subscriptionData.map((subscription, index) => (
// //                   <tr key={index}>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">
// //                       {subscription.SubscriptionName}
// //                     </td>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">
// //                       ${subscription.MonthlyAmount.toFixed(2)}
// //                     </td>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">
// //                       {format(new Date(subscription.StartDate), 'MMM dd, yyyy')}
// //                     </td>
// //                     <td className="py-2 px-4 border-b border-gray-200 text-sm">
// //                       {subscription.EndDate
// //                         ? format(new Date(subscription.EndDate), 'MMM dd, yyyy')
// //                         : 'Ongoing'}
// //                     </td>
// //                   </tr>
// //                 ))}
// //               </tbody>
// //             </table>
// //           </div>
// //         </>
// //       )}

// //       {alertMessage && <p className="text-red-500 mt-4">{alertMessage}</p>}
// //     </div>
// //   );
// // };

// // export default UsageStats;